import { c as _c } from "react/compiler-runtime";
/* eslint-disable jsx-a11y/click-events-have-key-events */
import kebabCase from 'lodash/kebabCase';
import type { ReactNode, JSX } from 'react';
import { useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { generateTestId } from '../../lib/test-helpers';
import { colors } from '../../theme/colors';
import { CheckboxInput } from '../checkbox-input';
import { FadeIn } from '../fade-in';
import { Flexbox, FlexCell } from '../flexbox';
import { IconButton } from '../icon-button';
import { IconRadioButton } from '../icons';
import type { MenuItem } from '../menu-2';
import { Menu } from '../menu-2';
import { Padding } from '../padding';
import { Popper } from '../popper';
import { Spacing } from '../spacing';
import { Stack } from '../stack';
import { Text } from '../text';
import { TextEyebrow } from '../text-eyebrow';
import { TextLink } from '../text-link';
import ToggleDetailsButton from '../toggle-details-button';
export interface FeaturedCardProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  content?: ReactNode;
  isExpandable?: boolean;
  defaultExpanded?: boolean;
  isSelected?: boolean;
  target?: string;
  icon?: ReactNode;
  href?: string;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  indeterminate?: boolean;
  actions?: ReactNode;
  badge?: ReactNode;
  selectType?: 'radio' | 'checkbox';
  onSelect?: () => void;
  onRemove?: () => void;
  hasShadow?: boolean;
  isRounded?: boolean;
  menuItems?: MenuItem[];
  testId?: string;
}
/**
 * @deprecated use `DetailCard` instead to follow current design system
 */
export default function FeaturedCard(t0) {
  const $ = _c(63);
  const {
    icon,
    eyebrow,
    title,
    target,
    href,
    onClick,
    subtitle,
    content,
    isExpandable,
    indeterminate,
    defaultExpanded: t1,
    actions,
    badge,
    onSelect,
    isSelected,
    selectType: t2,
    onRemove,
    hasShadow: t3,
    isRounded: t4,
    menuItems,
    testId: propTestId
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  const selectType = t2 === undefined ? "checkbox" : t2;
  const hasShadow = t3 === undefined ? false : t3;
  const isRounded = t4 === undefined ? true : t4;
  const isSelectable = !!onSelect;
  const [expanded, setExpanded] = useState(defaultExpanded);
  let t5;
  if ($[0] !== propTestId || $[1] !== title) {
    t5 = propTestId ?? generateTestId(kebabCase(title), "feature-card");
    $[0] = propTestId;
    $[1] = title;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const testId = t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function toggleExpanded() {
      setExpanded(_temp);
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const toggleExpanded = t6;
  let t7;
  if ($[4] !== indeterminate || $[5] !== isSelectable || $[6] !== isSelected || $[7] !== onSelect || $[8] !== selectType) {
    t7 = isSelectable && <div onClick={selectType === "radio" ? onSelect : undefined} role="checkbox" aria-checked={isSelected} tabIndex={0} style={{
      marginRight: 12,
      outline: 0,
      cursor: "pointer"
    }}>{selectType === "checkbox" ? <CheckboxInput checked={isSelected} indeterminate={indeterminate} onChange={onSelect} /> : <IconRadioButton isActive={isSelected} />}</div>;
    $[4] = indeterminate;
    $[5] = isSelectable;
    $[6] = isSelected;
    $[7] = onSelect;
    $[8] = selectType;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== icon) {
    t8 = icon && <div style={{
      marginRight: 12,
      display: "flex"
    }}>{icon}</div>;
    $[10] = icon;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== eyebrow) {
    t9 = <TextEyebrow color={colors.peach[500]}>{eyebrow}</TextEyebrow>;
    $[12] = eyebrow;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Spacing height={4} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isSelectable ? undefined : href;
  const t12 = isSelectable ? onSelect : onClick;
  const t13 = isSelectable ? undefined : target;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      paddingRight: 16
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t11 || $[17] !== t12 || $[18] !== t13 || $[19] !== title) {
    t15 = <TextLink weight={600} variant="secondary" href={t11} onClick={t12} target={t13} colorOnHover={colors.brand[400]} underline={false} underlineOnHover={false} style={t14}>{title}</TextLink>;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = title;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== subtitle) {
    t16 = subtitle && <Text color="default" size="regular">{subtitle}</Text>;
    $[21] = subtitle;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t15 || $[24] !== t16 || $[25] !== t9) {
    t17 = <Flexbox flexDirection="column">{t9}{t10}{t15}{t16}</Flexbox>;
    $[23] = t15;
    $[24] = t16;
    $[25] = t9;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== actions || $[28] !== badge || $[29] !== menuItems || $[30] !== onRemove || $[31] !== title) {
    t18 = (actions || badge || onRemove) && <Stack direction="horizontal" gap={12} alignItems="center">{actions && <div>{actions}</div>}{badge ? <StyledBadge>{badge}</StyledBadge> : null}{onRemove && <IconButton type="close" onClick={() => onRemove()} ariaLabel={`Remove ${title}`} />}{menuItems && <Popper trigger={_temp2}>{() => <Menu items={menuItems} />}</Popper>}</Stack>;
    $[27] = actions;
    $[28] = badge;
    $[29] = menuItems;
    $[30] = onRemove;
    $[31] = title;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <FlexCell flex={1}><Flexbox justifyContent="space-between">{t17}{t18}</Flexbox></FlexCell>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t19 || $[37] !== t7 || $[38] !== t8) {
    t20 = <Flexbox alignItems="center">{t7}{t8}{t19}</Flexbox>;
    $[36] = t19;
    $[37] = t7;
    $[38] = t8;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = isSelectable ? 32 : 0;
  let t22;
  if ($[40] !== content) {
    t22 = content && <Spacing height={16} />;
    $[40] = content;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== content || $[43] !== expanded || $[44] !== isExpandable) {
    t23 = (expanded || !isExpandable) && <FadeIn duration={isExpandable ? "0.2s" : "0"}>{content}</FadeIn>;
    $[42] = content;
    $[43] = expanded;
    $[44] = isExpandable;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== expanded || $[47] !== isExpandable) {
    t24 = isExpandable && <>{expanded && <Spacing height={12} />}<ToggleDetailsButton showing={expanded} onClick={toggleExpanded} /></>;
    $[46] = expanded;
    $[47] = isExpandable;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t24) {
    t25 = <Padding left={t21}>{t22}{t23}{t24}</Padding>;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t20 || $[55] !== t25) {
    t26 = <Flexbox><FlexCell flex={1}>{t20}{t25}</FlexCell></Flexbox>;
    $[54] = t20;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== hasShadow || $[58] !== isRounded || $[59] !== isSelectable || $[60] !== t26 || $[61] !== testId) {
    t27 = <StyledFeaturedCard $isSelectable={isSelectable} $isRounded={isRounded} $hasShadow={hasShadow} data-testid={testId}>{t26}</StyledFeaturedCard>;
    $[57] = hasShadow;
    $[58] = isRounded;
    $[59] = isSelectable;
    $[60] = t26;
    $[61] = testId;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  return t27;
}
function _temp2(t0) {
  const {
    toggle,
    triggerRef
  } = t0;
  return <IconButton type="more" innerRef={triggerRef} onClick={toggle} />;
}
function _temp(currentExpanded) {
  return !currentExpanded;
}
const StyledFeaturedCard = styled.div<{
  $isSelectable: boolean;
  $isRounded: boolean;
  $hasShadow: boolean;
}>(({
  $isSelectable,
  $isRounded,
  $hasShadow
}) => `
      ${$isSelectable ? `border-bottom: 1px solid #dbdbdb;` : `border: 1px solid ${colors.steel[200]};`}
      ${$isSelectable || !$isRounded ? 'border-radius: 0px;' : 'border-radius: 4px;'}

      box-shadow: ${$hasShadow ? '0px 5px 10px rgba(0, 0, 0, 0.03)' : 'none'};

      background: #fff;
      position: relative;
      padding: 20px;
      text-align: left;
  `);
const StyledBadge = styled.div``;